import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";

import arrowIcon from "../../assets/previous_icon.png";
import checkIcon from "../../assets/check_icon.png";
import printIcon from "../../assets/printer_icon.png";

import CustomButton from "../../components/CustomButton";
import AreYouSureModal from "../../components/AreYouSureModal";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import useLanguage from "../../utils/useLanguage";
import PrintablePaperOrder from "./PrintablePaperOrder";

const PaperInvoiceList = ({ data, setData, invoiceStatus = "PENDING" }) => {
  // HOOKS
  const { t } = useTranslation("general");
  const navigate = useNavigate();
  const { currentDirection } = useLanguage();

  // STATES
  const [collapsed, setCollapsed] = useState({});
  const [loading, setLoading] = useState(false);
  const [areYouSureModal, setAreYouSureModal] = useState(false);
  const invoicePrintRef = useRef(null);
  const [orderStatus, setOrderStatus] = useState(data?.data?.invoiceStatus);

  // Transform backend response for display
  const transformedData = data?.data?.colors?.reduce((acc, item) => {
    const colorCode = item.colorCode;
    if (!colorCode) return acc;

    if (!acc[colorCode]) {
      acc[colorCode] = {
        code: colorCode,
        colorDetails: [],
      };
    }

    acc[colorCode].colorDetails.push({
      code: item.colorCode,
      colorName: item?.colorName,
      quantity: item?.transaction?.quantity,
      price: item?.transaction?.price,
      currency: item?.transaction?.currency,
      unitOfMeasure: item?.transaction?.unitOfMeasuring,
      amountOfRolls: item?.transaction?.amountOfRolls || 0,
    });

    return acc;
  }, {});

  const colorList = Object.values(transformedData || {});

  const toggleCollapse = (colorCode) => {
    setCollapsed((prev) => ({
      ...prev,
      [colorCode]: !prev[colorCode],
    }));
  };

  const handleConfirmInvoicePrepration = async () => {
    setLoading(true);
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_URL}/confirmInvoicePrepration/${data?.data?.id}`,
        {
          organizationId: data?.data?.organization?.id,
        }
      );

      if (response.status === 200) {
        setAreYouSureModal(false);
        navigate("/pending-orders");
      }
    } catch (error) {
      console.error("Error preparing invoice:", error);

      if (error.response?.status === 400) {
        alert(
          error.response.data.error || "Cannot prepare invoice at this time"
        );
      } else if (error.response?.status === 404) {
        alert("Invoice not found");
      } else {
        alert("Failed to prepare invoice. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAttemptToConfirm = () => setAreYouSureModal(true);

  const preparePrintData = () => {
    return colorList.map((color) => ({
      code: color.code,
      colors: color.colorDetails.map((detail) => ({
        colorCode: detail.code,
        colorName: detail.colorName,
        quantity: detail.quantity,
        amountOfRolls: detail.amountOfRolls,
        price: detail.price,
      })),
    }));
  };

  const handlePrint = useReactToPrint({
    contentRef: invoicePrintRef,
    pageStyle: `
      @page { 
          size: A4; 
          margin: 5mm;
          width: 21cm;
          height: 29.7cm;
          direction: ${currentDirection}
      }
      @media print {
        body { 
          -webkit-print-color-adjust: exact; 
          padding: 10mm;
        }
        .printable-order-view { 
          display: flex !important;
          direction: ${currentDirection}
        }
      }
    `,
  });

  return (
    <div className="c-fabric-table">
      {colorList.map((color) => (
        <div key={color.code} className="c-fabric-block u-margin--bottom_400">
          <div
            className="c-fabric-header"
            onClick={() => toggleCollapse(color.code)}
          >
            <span
              className="c-collapse-icon"
              style={{
                cursor: "pointer",
                transform: collapsed[color.code]
                  ? "rotate(0deg)"
                  : "rotate(-90deg)",
                transition: "transform 0.3s ease",
              }}
            >
              <img src={arrowIcon} alt="Collapse" style={{ width: 16 }} />
            </span>
            <span className="c-fabric-code">
              {t("code")}: {color.code}
            </span>
          </div>
          {!collapsed[color.code] && (
            <table className="c-fabric-inner-table">
              <thead>
                <tr>
                  <th>{t("colorCode")}</th>
                  <th>{t("colorName")}</th>
                  <th>{t("amountOfRolls")}</th>
                  <th>{t("quantity")}</th>
                </tr>
              </thead>
              <tbody>
                {color?.colorDetails?.map((detail) => (
                  <tr key={detail.code}>
                    <td>{detail?.code}</td>
                    <td>{detail?.colorName}</td>
                    <td>
                      {detail?.amountOfRolls} {t("rollUnit")}
                    </td>
                    <td>
                      {detail?.quantity} {t("ton")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}

      {invoiceStatus === "PENDING" && (
        <div className="u-padding--top_700 u-padding--bottom_700  o-row o-row--horizontal-align_end o-row--gap-size_400">
          <CustomButton
            variant="gray"
            title={t("print")}
            onClick={handlePrint}
            icon={printIcon}
            iconSize={18}
          />

          {data?.data?.invoiceStatus === "PREPARED" ? null : (
            <CustomButton
              title={t("confirmOrder")}
              variant="green"
              onClick={handleAttemptToConfirm}
              icon={checkIcon}
              iconSize={18}
            />
          )}
        </div>
      )}

      {areYouSureModal ? (
        <AreYouSureModal
          onClose={() => setAreYouSureModal(false)}
          onConfirm={handleConfirmInvoicePrepration}
          customMessage="invoicePrepareSureMessage"
          closeTitle="cancel"
          confirmTitle="confirm"
          confirmIconType="whiteCheck"

          loading={loading}
          confirm
        />
      ) : null}

      <PrintablePaperOrder
        ref={invoicePrintRef}
        data={preparePrintData()}
        currency={{ value: data?.data?.currency || "USD" }}
        measuringUnit={data?.data?.unitOfMeasure || "ton"}
        orderDetails={{
          orderNumber: data?.data?.id,
          date: new Date(data?.data?.date).toLocaleDateString(),
          companyName: data?.data?.organization?.organizationName,
          createdBy: data?.data?.user?.name,
        }}
      />
    </div>
  );
};

export default PaperInvoiceList;
